import { helpers, required } from '@vuelidate/validators'
import type { TInstance, IUseFileOptions } from '@/components/Inputs/Composables/validators/validators.types'

export function useFile(
  field: string,
  t: TInstance,
  {
    maxSize = 20, // in MB,
    required: isRequired = false,
    allowedExtensions = [],
  }: IUseFileOptions = {},
) {
  const normalizedAllowedExtensions = allowedExtensions
    .map(extension => extension.replace('.', '').trim().toLowerCase())
    .filter(Boolean)

  const allowedExtensionsLabel = normalizedAllowedExtensions
    .map(extension => `.${extension}`)
    .join(', ')

  const maxFileSize = helpers.withMessage(
    () => t('formValidatorMessages.file.maxSize', { maxSize }),
    (document: File | null) => {
      if (!document) {
        return !isRequired
      }

      return document.size <= maxSize * 1024 * 1024
    },
  )

  const allowedFileFormat = helpers.withMessage(
    () => t('formValidatorMessages.file.invalidFormat', { formats: allowedExtensionsLabel }),
    (document: File | null) => {
      if (!document || !normalizedAllowedExtensions.length) {
        return true
      }

      const extension = document.name.split('.').pop()?.toLowerCase()

      return !!extension && normalizedAllowedExtensions.includes(extension)
    },
  )

  return ({
    [`${field}`]: {
      ...(isRequired && {
        required: helpers.withMessage(
          () => t('formValidatorMessages.file.required'),
          required,
        ),
      }),
      maxSize: helpers.withMessage(
        () => t('formValidatorMessages.file.maxSize', { maxSize }),
        maxFileSize,
      ),
      ...(normalizedAllowedExtensions.length && {
        invalidFormat: helpers.withMessage(
          () => t('formValidatorMessages.file.invalidFormat', { formats: allowedExtensionsLabel }),
          allowedFileFormat,
        ),
      }),
    },
  })
}
